import React, { useState, useEffect } from 'react';
import { academyAdminAPI } from '../../lib/academyApi';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Trash2, Eye, Award, FileText } from 'lucide-react';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700', registered: 'bg-blue-100 text-blue-700', approved: 'bg-indigo-100 text-indigo-700',
  paid: 'bg-green-100 text-green-700', awaiting_contact: 'bg-orange-100 text-orange-700', contacted: 'bg-cyan-100 text-cyan-700',
  scheduled: 'bg-purple-100 text-purple-700', completed: 'bg-emerald-100 text-emerald-700', cancelled: 'bg-red-100 text-red-700',
};
const statusLabels = {
  pending: 'En attente', registered: 'Inscrit', approved: 'Approuve', paid: 'Paye', awaiting_contact: 'A contacter',
  contacted: 'Contacte', scheduled: 'Planifie', completed: 'Complete', cancelled: 'Annule',
};
const paymentLabels = { pending: 'En attente', paid: 'Paye', invoice: 'Facture', initiated: 'Initie', failed: 'Echoue', refunded: 'Rembourse', expired: 'Expire' };
const allStatuses = ['pending','registered','approved','paid','awaiting_contact','contacted','scheduled','completed','cancelled'];

const AcademyRegistrations = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [generating, setGenerating] = useState(null);

  const fetchData = async () => {
    try { const r = await academyAdminAPI.listRegistrations(filter ? { status: filter } : {}); setItems(r.data); }
    catch(e){} finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, [filter]);

  const updateStatus = async (id, status) => {
    try { await academyAdminAPI.updateRegistration(id, { status }); toast.success('Statut mis a jour'); fetchData(); }
    catch(e) { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette inscription?')) return;
    try { await academyAdminAPI.deleteRegistration(id); toast.success('Supprimee'); fetchData(); setSelected(null); }
    catch(e) { toast.error('Erreur'); }
  };

  const handleGenerateCert = async (regId) => {
    setGenerating(regId);
    try { await academyAdminAPI.generateCertificate(regId); toast.success('Certificat genere'); fetchData(); }
    catch(e) { toast.error(e.response?.data?.detail || 'Erreur de generation'); }
    finally { setGenerating(null); }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" /></div>;

  return (
    <div className="p-6 lg:p-8" data-testid="academy-registrations-admin">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-[#1D1D1B]">Inscriptions</h1>
        <p className="text-neutral-500 text-sm">{items.length} inscription(s)</p>
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        <Button variant={!filter ? 'default' : 'outline'} size="sm" onClick={() => setFilter('')}>Toutes</Button>
        {allStatuses.map(s => <Button key={s} variant={filter === s ? 'default' : 'outline'} size="sm" onClick={() => setFilter(s)}>{statusLabels[s]}</Button>)}
      </div>
      {items.length === 0 ? (
        <p className="text-neutral-500 text-center py-12">Aucune inscription</p>
      ) : (
        <div className="space-y-3">
          {items.map(item => {
            const name = `${item.first_name || ''} ${item.last_name || ''}`.trim() || item.name || 'N/A';
            return (
              <Card key={item.id} className="hover:shadow-md transition-shadow" data-testid={`registration-${item.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-medium text-[#1D1D1B]">{name}</h3>
                      <p className="text-sm text-neutral-500">{item.email} {item.company && `- ${item.company}`}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={statusColors[item.status]}>{statusLabels[item.status] || item.status}</Badge>
                      {item.payment_status && item.payment_status !== 'pending' && (
                        <Badge variant="outline" className="text-xs">{paymentLabels[item.payment_status] || item.payment_status}</Badge>
                      )}
                      {item.certificate_generated && <Award className="w-4 h-4 text-[#F9B438]" />}
                      <Button variant="ghost" size="sm" onClick={() => setSelected(selected === item.id ? null : item.id)}><Eye className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(item.id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  <div className="flex gap-4 text-xs text-neutral-400 flex-wrap">
                    <span>Formation: {item.training_title || item.training_id}</span>
                    {item.session_title && <span>Session: {item.session_title}</span>}
                    <span>{item.num_participants} participant(s)</span>
                    {item.payment_method === 'invoice' && <span className="text-blue-500">Facture</span>}
                    {item.amount_paid > 0 && <span className="text-green-600">{item.amount_paid} $ paye</span>}
                    <span>{new Date(item.created_at).toLocaleDateString('fr-CA')}</span>
                  </div>
                  {selected === item.id && (
                    <div className="mt-4 pt-4 border-t space-y-3 text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <p><span className="font-medium">Telephone:</span> {item.phone || '-'}</p>
                        <p><span className="font-medium">Entreprise:</span> {item.company || '-'}</p>
                        <p><span className="font-medium">No. ordre pro:</span> {item.professional_order_number || '-'}</p>
                        <p><span className="font-medium">Mode paiement:</span> {item.payment_method || 'stripe'}</p>
                      </div>
                      {item.comments && <p><span className="font-medium">Commentaires:</span> {item.comments}</p>}
                      <div className="flex gap-2 pt-2 flex-wrap">
                        <span className="text-xs text-neutral-500 self-center mr-2">Changer statut:</span>
                        {allStatuses.filter(s => s !== item.status).slice(0, 5).map(s => (
                          <Button key={s} variant="outline" size="sm" className="text-xs" onClick={() => updateStatus(item.id, s)}>{statusLabels[s]}</Button>
                        ))}
                      </div>
                      {!item.certificate_generated && ['paid','completed','registered','approved'].includes(item.status) && (
                        <Button variant="outline" size="sm" onClick={() => handleGenerateCert(item.id)} disabled={generating === item.id} data-testid={`gen-cert-${item.id}`}>
                          {generating === item.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Award className="w-3 h-3 mr-1" />}
                          Generer certificat
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AcademyRegistrations;
